// src/pages/JewelleryPlans/Redemptions/QuoteDetails.jsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PrinterIcon,
  RefreshIcon,
  ScaleIcon,
  CurrencyRupeeIcon,
  SparklesIcon,
  LockClosedIcon,
  InformationCircleIcon,
  ShoppingBagIcon,
} from "@heroicons/react/outline";
import quoteService from "../../../services/jewelleryPlan/quoteService";
import redemptionService from "../../../services/jewelleryPlan/redemptionService";

const QuoteDetails = () => {
  const { planId, quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [redemptionMode, setRedemptionMode] = useState("CREDIT"); // "CREDIT" or "ITEMS"

  useEffect(() => {
    fetchQuoteDetails();
  }, [quoteId]);

  useEffect(() => {
    if (quote && !validation?.expired) {
      const timer = setInterval(() => {
        setTimeRemaining(getTimeRemaining());
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [quote, validation]);

  const fetchQuoteDetails = async () => {
    try {
      setLoading(true);
      const [quoteResponse, validationResponse] = await Promise.all([
        quoteService.getQuote(quoteId),
        quoteService.validateQuote(quoteId),
      ]);

      setQuote(quoteResponse.data);
      setValidation(validationResponse.data);
    } catch (error) {
      console.error("Failed to fetch quote:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleValueRedemption = async () => {
    if (!validation?.valid) {
      alert("This quote is no longer valid. Please create a new quote.");
      return;
    }

    try {
      setCreating(true);
      const redemptionData = {
        planId,
        quoteId,
        // No items - this creates a VALUE_REDEMPTION
        additionalPaymentDetails: {
          method: "PENDING",
        },
        notes: `Value redemption created from quote ${quote.quoteNumber}`,
      };

      const result = await redemptionService.createRedemption(redemptionData);
      navigate(`/jewellery/redemptions/${result.data._id}`);
    } catch (error) {
      console.error("Failed to create redemption:", error);
      alert("Failed to create redemption. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleItemRedemption = () => {
    // Navigate to item selection page with quote context
    navigate(`/jewellery/${planId}/select-items?quoteId=${quoteId}`);
  };

  const getTimeRemaining = () => {
    if (!quote?.expiresAt) return "Expired";
    const now = new Date();
    const expires = new Date(quote.expiresAt);
    const diff = expires - now;

    if (diff <= 0) return "Expired";

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  // Get plan type display name
  const getPlanTypeDisplay = (type) => {
    switch (type) {
      case "VALUE_SAVINGS":
        return "Fixed Value Plan";
      case "WEIGHT_SAVINGS":
        return "Gold Weight Plan";
      case "PRICE_LOCK":
        return "Price Lock Plan";
      default:
        return type;
    }
  };

  // Get rate strategy display
  const getRateStrategyDisplay = (strategy) => {
    switch (strategy) {
      case "CURRENT_WITH_BENEFITS":
        return { text: "Current Rate + Benefits", color: "green" };
      case "CURRENT_NO_BENEFITS":
        return { text: "Current Rate (No Benefits)", color: "yellow" };
      case "MATURITY_LOCKED":
        return { text: "Maturity Rate (Locked)", color: "red" };
      case "FIXED_VALUE":
        return { text: "Fixed Value Plan", color: "blue" };
      case "PRICE_LOCKED":
        return { text: "Price Locked", color: "purple" };
      default:
        return { text: strategy || "Unknown", color: "gray" };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-500">Quote not found</p>
          <button
            onClick={() => navigate(`/jewellery/${planId}`)}
            className="mt-4 text-yellow-600 hover:text-yellow-700"
          >
            Back to Plan
          </button>
        </div>
      </div>
    );
  }

  const balanceSnapshot = quote.balanceSnapshot;
  const redemptionContext =
    quote.redemptionContext || balanceSnapshot?.redemptionContext;
  const planType = quote.planType || balanceSnapshot?.planDetails?.planType;
  const isValuePlan = planType === "VALUE_SAVINGS";
  const isWeightPlan = planType === "WEIGHT_SAVINGS";
  const isPriceLockPlan = planType === "PRICE_LOCK";
  const rateStrategy = getRateStrategyDisplay(quote.rateSnapshot?.strategy);

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => navigate(`/jewellery/${planId}`)}
            className="text-gray-500 hover:text-gray-700"
          >
            ← Back to Plan
          </button>
        </div>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Quote #{quote.quoteNumber}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Created on {formatDate(quote.createdAt)}
            </p>
            {planType && (
              <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {getPlanTypeDisplay(planType)}
              </div>
            )}
          </div>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            Print
          </button>
        </div>
      </div>

      {/* Status Alert */}
      <div
        className={`mb-6 rounded-lg p-4 ${
          validation?.valid
            ? "bg-green-50 border border-green-200"
            : "bg-red-50 border border-red-200"
        }`}
      >
        <div className="flex items-center">
          {validation?.valid ? (
            <>
              <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">
                  Quote is valid and ready for redemption
                </p>
                <div className="flex items-center mt-1">
                  <ClockIcon className="h-4 w-4 text-green-700 mr-1" />
                  <p className="text-xs text-green-700">
                    Time remaining: {timeRemaining}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <ExclamationCircleIcon className="h-6 w-6 text-red-600 mr-3" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">
                  Quote has{" "}
                  {validation?.expired
                    ? "expired"
                    : validation?.used
                    ? "been used"
                    : "become invalid"}
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Please create a new quote to proceed with redemption
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Balance Summary Cards - Conditional by plan type */}
          {isValuePlan ? (
            // VALUE_SAVINGS Layout (2 cards)
            <div className="grid grid-cols-2 gap-4">
              {/* Locked Value */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <CurrencyRupeeIcon className="h-8 w-8 text-green-600" />
                  <LockClosedIcon className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  ₹{(quote.breakdown?.valueInr || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Locked Savings Value
                </p>
              </div>

              {/* Making Charge Benefit */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <SparklesIcon className="h-8 w-8 text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-gray-900">
                  {quote.breakdown?.benefitWaivePercent || 0}%
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Making Charge Waiver
                </p>
              </div>
            </div>
          ) : (
            // WEIGHT_SAVINGS / PRICE_LOCK Layout (3 cards)
            <div className="grid grid-cols-3 gap-4">
              {/* Total Grams */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <ScaleIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">
                  {quote.breakdown?.totalGrams || 0}g
                </p>
                <p className="text-xs text-gray-600 mt-1">Total Gold</p>
              </div>

              {/* Locked Value */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <CurrencyRupeeIcon className="h-8 w-8 text-green-600" />
                  {isPriceLockPlan && (
                    <LockClosedIcon className="h-4 w-4 text-green-600 ml-1" />
                  )}
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  ₹{(quote.breakdown?.lockedValue || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {isPriceLockPlan ? "Locked" : "Current"} Value (
                  {quote.selectedKarat}K)
                </p>
              </div>

              {/* Benefits */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <SparklesIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">
                  +{quote.breakdown?.benefitGrams || 0}g
                </p>
                <p className="text-xs text-gray-600 mt-1">Bonus Grams</p>
              </div>
            </div>
          )}

          {/* Redemption Context */}
          {redemptionContext && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Redemption Status
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Tier:</span>
                  <span
                    className={`text-sm font-medium px-2 py-1 rounded ${
                      redemptionContext.tier === "WITHIN_WINDOW"
                        ? "bg-green-100 text-green-800"
                        : redemptionContext.tier === "GRACE_PERIOD"
                        ? "bg-yellow-100 text-yellow-800"
                        : redemptionContext.tier === "EXPIRED"
                        ? "bg-red-100 text-red-800"
                        : redemptionContext.tier === "NOT_MATURED"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {redemptionContext.tier}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Window Status:</span>
                  <span
                    className={`text-sm font-medium ${
                      redemptionContext.withinWindow
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {redemptionContext.withinWindow
                      ? "✅ Within Window"
                      : "⚠️ Outside Window"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Benefits Status:
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      redemptionContext.benefitsApplicable
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}
                  >
                    {redemptionContext.benefitsApplicable
                      ? "Applied ✅"
                      : "Forfeited ❌"}
                  </span>
                </div>

                {redemptionContext.daysSinceMaturity !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      Days Since Maturity:
                    </span>
                    <span className="text-sm font-medium">
                      {redemptionContext.daysSinceMaturity} days
                    </span>
                  </div>
                )}

                {redemptionContext.message && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-gray-700 italic">
                      {redemptionContext.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Balance Breakdown - Conditional by plan type */}
          {balanceSnapshot?.balance && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Balance Breakdown
              </h2>

              {isValuePlan ? (
                // VALUE_SAVINGS Breakdown
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">
                      Fixed Savings Value:
                    </span>
                    <span className="text-sm font-medium">
                      ₹
                      {(balanceSnapshot.balance.valueInr || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">
                      Payments Completed:
                    </span>
                    <span className="text-sm font-medium">
                      {balanceSnapshot.balance.paymentsCompleted || 0}
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-sm text-gray-600">
                      Making Charge Benefit:
                    </span>
                    <span className="text-sm font-medium text-purple-600">
                      {quote.breakdown?.benefitWaivePercent || 0}% Waiver
                    </span>
                  </div>

                  {balanceSnapshot.balance.note && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                        <p className="text-xs text-blue-800">
                          {balanceSnapshot.balance.note}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // WEIGHT_SAVINGS / PRICE_LOCK Breakdown
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">
                      Accumulated Grams:
                    </span>
                    <span className="text-sm font-medium">
                      {balanceSnapshot.balance.accumulatedGrams || 0}g
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">
                      Benefit Grams:
                    </span>
                    <span className="text-sm font-medium text-green-600">
                      {balanceSnapshot.balance.benefitGrams || 0}g
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-base font-medium">
                      Total Available:
                    </span>
                    <span className="text-base font-bold">
                      {balanceSnapshot.balance.totalGrams || 0}g
                    </span>
                  </div>

                  {isPriceLockPlan && balanceSnapshot.balance.lockedRate && (
                    <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-start">
                        <LockClosedIcon className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0" />
                        <div className="text-xs text-purple-800">
                          <p className="font-medium">Rate Locked:</p>
                          <p className="mt-1">
                            ₹
                            {(
                              balanceSnapshot.balance.lockedRate || 0
                            ).toLocaleString()}
                            /gram
                          </p>
                          {balanceSnapshot.balance.note && (
                            <p className="mt-1 italic">
                              {balanceSnapshot.balance.note}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Rate Info - FIXED: No longer accessing rates array */}
          {quote.rateSnapshot && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {isValuePlan ? "Reference Rate Info" : "Locked Gold Rate"}
              </h2>

              {/* Rate Strategy Badge */}
              <div className="mb-4">
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-${rateStrategy.color}-100 text-${rateStrategy.color}-800`}
                >
                  {rateStrategy.text}
                </span>
              </div>

              {/* FIXED: Rate Details from breakdown instead of rateSnapshot.rates */}
              {!isValuePlan && quote.breakdown?.lockedRate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">
                      {quote.selectedKarat}K Gold Rate
                    </p>
                    {quote.rateSnapshot.source && (
                      <span className="text-xs text-gray-400">
                        Source: {quote.rateSnapshot.source}
                      </span>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    ₹{quote.breakdown.lockedRate.toLocaleString()}/gram
                  </p>
                  {quote.rateSnapshot.appliedAt && (
                    <p className="text-xs text-gray-500 mt-2">
                      Applied: {formatDate(quote.rateSnapshot.appliedAt)}
                    </p>
                  )}
                </div>
              )}

              {/* Alternative: Show from rateSnapshot if karat is present */}
              {!isValuePlan &&
                !quote.breakdown?.lockedRate &&
                quote.rateSnapshot?.karat && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      {quote.rateSnapshot.karat}K Gold
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      ₹{(quote.rateSnapshot.ratePerGram || 0).toLocaleString()}
                      /gram
                    </p>
                    {quote.rateSnapshot.rateDate && (
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(quote.rateSnapshot.rateDate)}
                      </p>
                    )}
                    {quote.rateSnapshot.source && (
                      <p className="text-xs text-gray-400 mt-1">
                        Source: {quote.rateSnapshot.source}
                      </p>
                    )}
                  </div>
                )}

              {isValuePlan && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                    <p className="text-xs text-blue-800">
                      For VALUE_SAVINGS plans, your savings amount is fixed in
                      rupees and is not affected by gold rate changes. The rate
                      shown here is for reference only when purchasing jewelry.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Benefits Calculation Details */}
          {quote.benefitsCalculation && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Benefits Calculation
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Type:</span>
                  <span className="text-sm font-medium">
                    {quote.benefitsCalculation.type || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Applicable:</span>
                  <span
                    className={`text-sm font-medium ${
                      quote.benefitsCalculation.applicable
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}
                  >
                    {quote.benefitsCalculation.applicable ? "Yes ✅" : "No ❌"}
                  </span>
                </div>

                {isValuePlan &&
                  quote.benefitsCalculation.waivePercent !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">
                        Waiver Percent:
                      </span>
                      <span className="text-sm font-medium text-purple-600">
                        {quote.benefitsCalculation.waivePercent}%
                      </span>
                    </div>
                  )}

                {!isValuePlan &&
                  quote.benefitsCalculation.grams !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">
                        Bonus Grams:
                      </span>
                      <span className="text-sm font-medium text-green-600">
                        {quote.benefitsCalculation.grams}g
                      </span>
                    </div>
                  )}

                {quote.benefitsCalculation.description && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-gray-700 italic">
                      {quote.benefitsCalculation.description}
                    </p>
                  </div>
                )}

                {!quote.benefitsCalculation.applicable &&
                  quote.benefitsCalculation.reason && (
                    <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-800">
                        <strong>Reason:</strong>{" "}
                        {quote.benefitsCalculation.reason}
                      </p>
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions - NEW: Redemption Type Choice */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h2>
            <div className="space-y-4">
              {validation?.valid && !validation?.used ? (
                <>
                  {/* Redemption Mode Selection */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      How would you like to redeem?
                    </h3>
                    <div className="grid grid-cols-1 gap-3">
                      <button
                        onClick={() => setRedemptionMode("CREDIT")}
                        className={`p-3 border rounded-lg text-left transition-all ${
                          redemptionMode === "CREDIT"
                            ? "border-yellow-500 bg-yellow-50 ring-2 ring-yellow-200"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center mb-1">
                          <CurrencyRupeeIcon className="h-5 w-5 text-yellow-600 mr-2" />
                          <p className="text-sm font-medium text-gray-900">
                            Store Credit
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 ml-7">
                          Redeem value now, select jewelry later at store
                        </p>
                      </button>

                      {/* TODO: Support in the Feture*/}
                      {/* <button
                        onClick={() => setRedemptionMode("ITEMS")}
                        className={`p-3 border rounded-lg text-left transition-all ${
                          redemptionMode === "ITEMS"
                            ? "border-yellow-500 bg-yellow-50 ring-2 ring-yellow-200"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center mb-1">
                          <ShoppingBagIcon className="h-5 w-5 text-yellow-600 mr-2" />
                          <p className="text-sm font-medium text-gray-900">
                            Select Jewelry
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 ml-7">
                          Choose items and complete purchase now
                        </p>
                      </button> */}
                    </div>
                  </div>

                  {/* Action Button based on mode */}
                  {redemptionMode === "CREDIT" ? (
                    <button
                      onClick={handleValueRedemption}
                      disabled={creating}
                      className="w-full px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 flex items-center justify-center font-medium"
                    >
                      {creating ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Creating Redemption...
                        </>
                      ) : (
                        <>
                          <CurrencyRupeeIcon className="h-5 w-5 mr-2" />
                          Redeem as Store Credit
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={handleItemRedemption}
                      className="w-full px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center justify-center font-medium"
                    >
                      <ShoppingBagIcon className="h-5 w-5 mr-2" />
                      Select Jewelry Items
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={() => navigate(`/jewellery/${planId}/create-quote`)}
                  className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center font-medium"
                >
                  <RefreshIcon className="h-5 w-5 mr-2" />
                  Create New Quote
                </button>
              )}

              <button
                onClick={() => navigate(`/jewellery/${planId}`)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Back to Plan Details
              </button>
            </div>
          </div>

          {/* User Details - Add this in the sidebar section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Customer Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Name:</span>
                <span className="text-sm font-medium">
                  {quote.userId?.name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Email:</span>
                <span className="text-sm font-medium">
                  {quote.userId?.email || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Phone:</span>
                <span className="text-sm font-medium">
                  {quote.userId?.phoneNumber || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Quote Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Quote Information
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Quote Number:</span>
                <span className="font-medium">{quote.quoteNumber}</span>
              </div>
              {planType && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan Type:</span>
                  <span className="font-medium">
                    {getPlanTypeDisplay(planType)}
                  </span>
                </div>
              )}
              {!isValuePlan && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Selected Karat:</span>
                  <span className="font-medium">{quote.selectedKarat}K</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Lock Duration:</span>
                <span className="font-medium">{quote.lockMinutes} minutes</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Created:</span>
                <span className="font-medium text-xs">
                  {formatDate(quote.createdAt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Expires At:</span>
                <span className="font-medium text-red-600 text-xs">
                  {formatDate(quote.expiresAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Plan Details Card */}
          {balanceSnapshot?.planDetails && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Plan Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan Number:</span>
                  <span className="font-medium">
                    {balanceSnapshot.planDetails.planNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`font-medium ${
                      balanceSnapshot.planDetails.status === "MATURED"
                        ? "text-green-600"
                        : "text-blue-600"
                    }`}
                  >
                    {balanceSnapshot.planDetails.status}
                  </span>
                </div>
                {balanceSnapshot.planDetails.maturedOn && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Matured On:</span>
                    <span className="font-medium text-xs">
                      {new Date(
                        balanceSnapshot.planDetails.maturedOn
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteDetails;
